import React from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Stack,
    useTheme,
    useMediaQuery,
    Avatar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useThemeMode } from '../contexts/ThemeContext';
import { useCatAssistant } from '../contexts/CatAssistantContext';

const Landing: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { mode } = useThemeMode();
    const isDark = mode === 'dark';
    const { say } = useCatAssistant();

    React.useEffect(() => {
        const timer = setTimeout(() => {
            say('¡Bienvenido a Turn.AR! 🐾 Soy Lyna, tu asistente felina móvil. Si no me conoces, ¡estoy aquí para que tu negocio brille! ✨', 'greeting', 10000);
        }, 2000);
        return () => clearTimeout(timer);
    }, [say]);

    const bgColor = isDark ? '#121212' : '#fff';
    const textPrimary = isDark ? '#E0E0E0' : '#2D3436';
    const textSecondary = isDark ? '#B0B0B0' : '#636E72';
    const sectionBg = isDark ? '#1A1A1A' : '#f8f9fa';
    const cardBg = isDark ? '#1E1E1E' : '#fff';
    const cardBorder = isDark ? '1px solid #333' : '1px solid rgba(0,0,0,0.02)';
    const cardShadow = isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.05)';
    const heroBg = isDark ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : 'linear-gradient(135deg, #fff 0%, #fff0f5 100%)';
    const stepCircleBg = isDark ? '#2A2A3A' : '#e6e6fa';
    const stepCircleColor = isDark ? '#C99FD6' : '#9B59B6';
    const headerText = isDark ? '#E0E0E0' : '#333';
    const footerBorder = isDark ? '#333' : '#eee';
    const footerText = isDark ? '#888' : '#999';
    const ctaBg = isDark ? '#1A1A2E' : '#2D3436';
    const demoBg = isDark ? 'rgba(155, 89, 182, 0.1)' : 'rgba(155, 89, 182, 0.05)';
    const dropShadow = isDark ? 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))' : 'drop-shadow(0 20px 40px rgba(0,0,0,0.12))';
    const dropShadow2 = isDark ? 'drop-shadow(0 15px 30px rgba(0,0,0,0.3))' : 'drop-shadow(0 15px 30px rgba(0,0,0,0.1))';

    const features = [
        {
            title: 'Para Todo Tipo de Negocio',
            description: 'Ya sea que alquiles cabañas, canchas de fútbol, castillos inflables o brindes servicios profesionales. Turn.AR se adapta a vos.',
            icon: <StorefrontIcon sx={{ fontSize: 40, color: '#FF6B9D' }} />,
        },
        {
            title: 'Notificaciones WhatsApp',
            description: 'Mantene a tus clientes informados automáticamente sobre sus reservas. Reducí el ausentismo y mejora la comunicación.',
            icon: <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366' }} />,
        },
        {
            title: 'Links Amigables',
            description: 'Crea tu identidad con URLs personalizadas (turn.ar/tu-agenda) para que tus clientes te encuentren fácil y rápido.',
            icon: <LinkIcon sx={{ fontSize: 40, color: '#9B59B6' }} />,
        },
        {
            title: 'Gestor de Tareas',
            description: 'No solo es una agenda. Organiza las tareas de tu equipo, mantenimiento y limpieza en la misma plataforma.',
            icon: <AssignmentIcon sx={{ fontSize: 40, color: '#FF6B9D' }} />,
        },
    ];

    return (
        <Box sx={{ bgcolor: bgColor, overflow: 'hidden' }}>
            {/* Header/Nav */}
            <Container maxWidth="lg">
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                            sx={{ bgcolor: '#FF6B9D', width: 40, height: 40, fontWeight: 'bold' }}
                        >
                            T
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: headerText, letterSpacing: -1 }}>
                            Turn.AR
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Button component={RouterLink} to="/login" sx={{ color: headerText, fontWeight: 600 }}>
                            Ingresar
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="contained"
                            sx={{
                                borderRadius: '20px',
                                bgcolor: '#9B59B6',
                                color: '#fff',
                                '&:hover': { bgcolor: '#8e44ad' },
                                textTransform: 'none',
                                px: 3
                            }}
                        >
                            Empezar Gratis
                        </Button>
                    </Stack>
                </Box>
            </Container>

            {/* Hero Section */}
            <Box
                sx={{
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 10, md: 15 },
                    background: heroBg,
                    position: 'relative'
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: '2.5rem', md: '4rem' },
                                    fontWeight: 800,
                                    color: textPrimary,
                                    lineHeight: 1.1,
                                    mb: 2
                                }}
                            >
                                Tu Agenda, <br />
                                <span style={{ color: '#FF6B9D' }}>en orden.</span> <br />
                                Como al Michi le gusta.
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{ color: textSecondary, mb: 4, fontWeight: 400, maxWidth: '500px' }}
                            >
                                Gestiona cualquier emprendimiento o negocio. Y cuando el michi dice todo, se refiere a: cabañas, canchas de fútbol, servicios o inflables. Crea agendas con URLs personalizadas y deja que Turn.AR y Michi organicen tu mundo.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    component={RouterLink}
                                    to="/register"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        borderRadius: '50px',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        bgcolor: '#FF6B9D',
                                        boxShadow: '0 10px 20px rgba(255, 107, 157, 0.3)',
                                        '&:hover': { bgcolor: '#e05688', boxShadow: '0 12px 24px rgba(255, 107, 157, 0.4)' },
                                        textTransform: 'none'
                                    }}
                                >
                                    Sumate ahora 🚀
                                </Button>
                                <Button
                                    component={RouterLink}
                                    to="/login"
                                    variant="outlined"
                                    size="large"
                                    sx={{
                                        borderRadius: '50px',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        borderColor: '#9B59B6',
                                        color: '#9B59B6',
                                        '&:hover': { borderColor: '#8e44ad', bgcolor: demoBg },
                                        textTransform: 'none'
                                    }}
                                >
                                    Ver Demo
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Box
                                component="img"
                                src="./landing_hero_cat_business_new.png"
                                alt="Michi Asistente"
                                sx={{
                                    width: '100%',
                                    maxWidth: '550px',
                                    filter: dropShadow,
                                    animation: 'float 6s ease-in-out infinite',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': { transform: 'scale(1.05) rotate(2deg)' }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>

                {/* Decorative elements */}
                <Box sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '-5%',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,107,157,0.1) 0%, rgba(255,107,157,0) 70%)',
                    zIndex: 0
                }} />
            </Box>

            {/* Features Section */}
            <Container maxWidth="lg" sx={{ py: 12 }}>
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography variant="overline" sx={{ color: '#9B59B6', fontWeight: 700, letterSpacing: 2 }}>
                        POR QUÉ ELEGIRNOS
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, color: textPrimary }}>
                        Todo lo que necesitas para brillar
                    </Typography>
                </Box>
                <Grid container spacing={4}>
                    {features.map((feature, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: '24px',
                                    boxShadow: cardShadow,
                                    transition: 'transform 0.3s ease',
                                    '&:hover': { transform: 'translateY(-10px)' },
                                    border: cardBorder,
                                    bgcolor: cardBg
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Box sx={{ mb: 2 }}>
                                        {feature.icon}
                                    </Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: textPrimary }}>
                                        {feature.title}
                                    </Typography>
                                    <Typography sx={{ color: textSecondary, lineHeight: 1.6 }}>
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* How it works / Secondary Section */}
            <Box sx={{ py: 10, bgcolor: sectionBg, position: 'relative', overflow: 'hidden' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                component="img"
                                src="./landing_secondary_cat.png"
                                alt="Michi Trabajando"
                                sx={{
                                    width: '100%',
                                    maxWidth: '500px',
                                    filter: dropShadow2,
                                    borderRadius: '20px',
                                    transform: 'perspective(1000px) rotateY(5deg)',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': { transform: 'perspective(1000px) rotateY(0deg) scale(1.02)' }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="overline" sx={{ color: '#FF6B9D', fontWeight: 700, letterSpacing: 2 }}>
                                SIMPLE Y PODEROSO
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 3, color: textPrimary }}>
                                Tu negocio online en 3 pasos
                            </Typography>
                            <Stack spacing={3}>
                                {[
                                    { title: 'Crea tu Agenda', text: 'Registrate y elegí tu URL personalizada (turn.ar/mi-agenda).' },
                                    { title: 'Configura tus Agendas', text: 'Crea espacios para tus canchas, cabañas o servicios.' },
                                    { title: 'Automatiza todo', text: 'Compartí tu link y deja que las reservas y notificaciones fluyan.' }
                                ].map((step, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{
                                            minWidth: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            bgcolor: stepCircleBg,
                                            color: stepCircleColor,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold'
                                        }}>
                                            {idx + 1}
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
                                                {step.title}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: textSecondary }}>
                                                {step.text}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Testimonials / Social Proof (Placeholder for future) */}
            <Container maxWidth="lg" sx={{ py: 12 }}>
            </Container>

            {/* CTA Section */}
            <Box sx={{ bgcolor: ctaBg, py: 10, color: '#fff' }}>
                <Container maxWidth="md">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography variant="h3" sx={{ fontWeight: 800 }}>
                            ¿Listo para transformar tu agenda?
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8, maxWidth: '600px' }}>
                            Unite a los cientos de empresas, autónomos y emprendedores que ya están usando Turn.AR para automatizar sus turnos.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="contained"
                            size="large"
                            sx={{
                                borderRadius: '50px',
                                px: 6,
                                py: 2,
                                fontSize: '1.2rem',
                                bgcolor: '#FF6B9D',
                                color: '#fff',
                                '&:hover': { bgcolor: '#e05688' },
                                textTransform: 'none'
                            }}
                        >
                            Empezar mi camino gratis <RocketLaunchIcon sx={{ ml: 1 }} />
                        </Button>
                    </Stack>
                </Container>
            </Box>
            <Box sx={{ py: 4, borderTop: `1px solid ${footerBorder}` }}>
                <Container maxWidth="lg">
                    <Typography variant="body2" sx={{ color: footerText, textAlign: 'center' }}>
                        &copy; {new Date().getFullYear()} Turn.AR. Hecho con amor y muchos Michis.
                    </Typography>
                </Container>
            </Box>

            <style>
                {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
          }
        `}
            </style>
        </Box>
    );
};

export default Landing;
